import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AuthApiService from 'services/AuthApiService'
import { fetchAvailableRoles, fetchUsersList } from 'store/userSlice'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { notifyUser } from 'store/notificationsSlice'
import {
	createBlankState,
	createDefaultErrorState,
	createUpdateUserPayload,
	getAvatarErrorMessage,
	getErrorInfoFromResponse,
	getNormalizedPhone,
	isUserStateDirty,
	mapUserResponseToFormState,
	validateEmail,
	validateFullname,
	validatePhone,
	validatePosition,
} from '../helpers/settingUserHelpers'
import { ErrorResponseType, UserFormState } from '../types/settingUserTypes'

type UseSettingUserArgs = {
	onClose: () => void
	open: boolean
	userId?: number
}

export const useSettingUser = ({
	onClose,
	open,
	userId,
}: UseSettingUserArgs) => {
	const dispatch = useAppDispatch()
	const params = useParams<{ userId?: string }>()
	const navigate = useNavigate()

	const paramUserId = params.userId ? Number(params.userId) : undefined
	const currentUserId =
		userId ?? (Number.isNaN(paramUserId) ? undefined : paramUserId)

	const { availableRoles, availableRolesPending } = useAppSelector(
		state => state.userReducer,
	)

	const [userData, setUserData] = useState<UserFormState>(createBlankState())
	const [initialState, setInitialState] =
		useState<UserFormState>(createBlankState())
	const [newPassword, setNewPassword] = useState('')
	const [serverErrorText, setServerErrorText] = useState('')
	const [isCreateRolePopup, setIsCreateRolePopup] = useState(false)
	const [openErrorPopup, setOpenErrorPopup] = useState(false)
	const [downloadUserInfo, setDownloadUserInfo] = useState(false)
	const [errorStateMessages, setErrorStateMessages] =
		useState<ErrorResponseType>({})
	const [errorState, setErrorState] = useState(createDefaultErrorState())

	const isDirty = useMemo(
		() => isUserStateDirty(userData, initialState),
		[userData, initialState],
	)

	const updateUserDataField = <K extends keyof UserFormState>(
		field: K,
		value: UserFormState[K],
	) => {
		setUserData(prev => ({ ...prev, [field]: value }))
	}

	const validators = useMemo(
		() => ({
			fullname: () => {
				const isError = validateFullname(userData.fullname)
				setErrorState(prev =>
					prev.fullname === isError ? prev : { ...prev, fullname: isError },
				)
			},
			email: () => {
				const isError = validateEmail(userData.email)
				setErrorState(prev =>
					prev.email === isError ? prev : { ...prev, email: isError },
				)
			},
			phoneNum: () => {
				const isError = validatePhone(userData.mobile_phone)
				setErrorState(prev =>
					prev.phoneNum === isError ? prev : { ...prev, phoneNum: isError },
				)
			},
			position: () => {
				const isError = validatePosition(userData.position)
				setErrorState(prev =>
					prev.position === isError ? prev : { ...prev, position: isError },
				)
			},
		}),
		[userData],
	)

	useEffect(() => {
		if (availableRoles.length === 0) {
			dispatch(fetchAvailableRoles())
		}
	}, [availableRoles.length, dispatch])

	useEffect(() => {
		if (!open) return

		if (!currentUserId) {
			setUserData(createBlankState())
			setInitialState(createBlankState())
			setNewPassword('')
			setServerErrorText('')
			setErrorState(createDefaultErrorState())
			return
		}

		setServerErrorText('')
		setErrorState(createDefaultErrorState())
		setDownloadUserInfo(true)
		AuthApiService.getUserInfoById(currentUserId)
			.then(response => {
				const mapped = mapUserResponseToFormState(response.data)
				setUserData(mapped)
				setInitialState(mapped)
			})
			.finally(() => setDownloadUserInfo(false))
	}, [open, currentUserId])

	const updateAvatarIfNeeded = async (targetUserId: number) => {
		if (userData.avatar === initialState.avatar) return

		if (userData.avatar === null) {
			await AuthApiService.putChangeUserAvatar(
				targetUserId,
				{ avatar: null },
				undefined,
			)
			return
		}

		if (userData.avatar instanceof File) {
			const formData = new FormData()
			formData.append('avatar', userData.avatar, 'avatar.jpg')
			await AuthApiService.putChangeUserAvatar(targetUserId, formData, {
				'Content-Type': 'multipart/form-data',
			})
		}
	}

	const updateUserDataAtServer = async (targetUserId: number) => {
		const updateData = createUpdateUserPayload(
			userData,
			newPassword,
			availableRoles,
		)
		try {
			await updateAvatarIfNeeded(targetUserId)
		} catch (error) {
			const message = getAvatarErrorMessage(error)
			dispatch(
				notifyUser({
					title: userData.fullname,
					description: message ? message : 'Ошибка при изменении аватара',
					isError: true,
				}),
			)
		}

		AuthApiService.putChangeUserData(updateData, targetUserId)
			.then(() => {
				dispatch(
					notifyUser({
						title: userData.fullname,
						description: 'Изменения успешно сохранены',
						isError: false,
					}),
				)
				dispatch(fetchUsersList())
				onClose()
			})
			.catch(error => {
				const { message, isUsernameError } = getErrorInfoFromResponse(error)
				if (isUsernameError) setServerErrorText(message)
			})
	}

	const saveDataAtServer = () => {
		if (!userData.id) {
			const updateData = {
				username: getNormalizedPhone(userData.mobile_phone),
				password: newPassword,
			}
			AuthApiService.postNewUser(updateData)
				.then(response => {
					updateUserDataField('id', response.data.id)
					updateUserDataAtServer(response.data.id)
					dispatch(fetchUsersList())
					onClose()
				})
				.catch(error => {
					const { message } = getErrorInfoFromResponse(error)
					setServerErrorText(message)
				})
		} else {
			if (userData.id !== null) {
				updateUserDataAtServer(userData.id)
			}
		}

		if (currentUserId && newPassword && userData.id !== null) {
			const updateData = {
				id: userData.id,
				password: newPassword,
			}
			AuthApiService.postForcePassword(updateData)
				.then(responce => {
					if (responce.status === 202) {
						dispatch(
							notifyUser({
								title: userData.fullname,
								description: 'Пароль пользователя успешно изменен',
								isError: false,
							}),
						)
						navigate('/settings/management')
					}
				})
				.catch(error => {
					const { message } = getErrorInfoFromResponse(error)
					dispatch(
						notifyUser({
							title: userData.fullname,
							description: message
								? message
								: 'Ошибка при изменении пароля пользователя',
							isError: true,
						}),
					)
				})
		}
	}

	const changeCurrentRole = () => {
		setOpenErrorPopup(false)
		const newRoleTitle = availableRoles.find(
			roleId => roleId.id === errorStateMessages.payload?.role_id,
		)
		if (newRoleTitle) {
			updateUserDataField('role', newRoleTitle.title)
		}
	}

	const returnAtEditCreateRoleForm = () => {
		setOpenErrorPopup(false)
		setIsCreateRolePopup(true)
	}

	const returnAtEditEditUserForm = () => {
		setOpenErrorPopup(false)
	}

	const confirmExitWithoutChanges = () => {
		setOpenErrorPopup(false)
		navigate('/settings/management')
	}

	return {
		availableRoles,
		availableRolesPending,
		currentUserId,
		userData,
		newPassword,
		serverErrorText,
		isCreateRolePopup,
		openErrorPopup,
		downloadUserInfo,
		errorStateMessages,
		errorState,
		isDirty,
		setNewPassword,
		setServerErrorText,
		setIsCreateRolePopup,
		setOpenErrorPopup,
		setErrorStateMessages,
		setErrorState,
		updateUserDataField,
		saveDataAtServer,
		changeCurrentRole,
		returnAtEditCreateRoleForm,
		returnAtEditEditUserForm,
		confirmExitWithoutChanges,
		validators,
	}
}
